import { Add, Build, ContentCopy, Delete, Edit, Search } from "@mui/icons-material";
import {
    Box,
    Chip,
    Breadcrumbs,
    Button,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers"; // Importando o SweetAlert2
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import CustomPagination from "../../components/Pagination";
import PrintersOverviewPanel from '../../components/printers/PrintersOverviewPanel';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatUuid } from '../../helpers/formatters';

function Printers() {
    const [printers, setPrinters] = useState([]);
    const navigate = useNavigate(); // Usando useNavigate para redirecionamento
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchPrinters = useCallback(() => {
        setIsLoading(true); // Indica que o carregamento está em andamento
        api.get(`printers?page=${currentPage}&limit=${itemsPerPage}&q=${searchTerm}`)
            .then(response => {
                setPrinters(response.data.printers);
                setTotalPages(response.data.total_pages);
            })
            .catch(error => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false); // Finaliza o carregamento
            });
    }, [currentPage, itemsPerPage, searchTerm]);

    useListFetch(fetchPrinters, [currentPage, itemsPerPage, appliedSearchTerm, fetchPrinters]);

    const handleSearch = () => {
        setCurrentPage(1);
        setAppliedSearchTerm(true);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleCreatePrinter = () => {
        navigate('/admin/register-printer');  // Redireciona para a página de criação de impressora
    };

    const handleEditPrinter = (id) => {
        navigate(`/admin/edit-printer/${id}`);  // Redireciona para a página de edição
    };

    const handlePrinterMaintenances = (id) => {
        navigate(`/admin/printer-maintenances?printer_uuid=${id}`);
    };

    const handleCopyUuid = async (uuid) => {
        try {
            await navigator.clipboard.writeText(uuid);
            Swal.fire({
                title: 'Copiado!',
                text: 'UUID copiado. Use-o no Acompanha 3D Farm Link para sincronizar esta impressora.',
                icon: 'success',
                timer: 2200,
                showConfirmButton: false,
            });
        } catch {
            Swal.fire({
                title: 'Não foi possível copiar',
                text: 'Selecione o UUID manualmente e use Ctrl+C (ou Cmd+C).',
                icon: 'warning',
            });
        }
    };

    const handleDeletePrinter = (printerId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta impressora será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete, // Impede que o SweetAlert seja fechado durante o carregamento
        }).then((result) => {
            if (result.isConfirmed) {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`printers/${printerId}`)
                        .then(response => {
                            // Atualiza o estado após remoção
                            setPrinters(printers.filter(printer => printer.id !== printerId));
                            Swal.fire('Removido!', 'A impressora foi removida com sucesso.', 'success');
                            setLoadingDelete(false);
                        })
                        .catch(error => {
                            console.error('Erro ao remover a impressora:', error);
                            Swal.fire('Erro!', 'Houve um problema ao remover a impressora.', 'error');
                            setLoadingDelete(false);
                        });
                }
            }
        });
    };

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Impressoras</Typography>
            </Breadcrumbs>

            <PrintersOverviewPanel />

            <Box
                sx={{
                    mt: 4,
                    pt: 3,
                    borderTop: 1,
                    borderColor: 'divider',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'stretch' : 'center',
                        marginBottom: 2,
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: 'center',
                            gap: 2,
                            flex: 1,
                        }}
                    >
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Buscar impressora..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            fullWidth={isMobile}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSearch}
                            startIcon={<Search />}
                            fullWidth={isMobile}
                        >
                            Buscar
                        </Button>
                        {appliedSearchTerm && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleClearSearch}
                                startIcon={<ClearIcon />}
                                fullWidth={isMobile}
                            >
                                Limpar Busca
                            </Button>
                        )}
                    </Box>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreatePrinter}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                        sx={{ flexShrink: 0 }}
                    >
                        Nova impressora
                    </Button>
                </Box>

                {isLoading && <Loading />}

                {didNotLoad && !isLoading && <DidNotLoad />}

                {!isLoading && !didNotLoad && (
                    <>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>UUID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Marca</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Modelo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Consumo máximo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Valor pago</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(printers) && printers.length > 0 && printers.map((printer) => (
                            <TableRow key={printer.id}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Tooltip title={printer.id}>
                                            <Typography
                                                variant="body2"
                                                component="span"
                                                sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                                            >
                                                {formatUuid(printer.id)}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title="Copiar UUID completo para o Farm Link">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleCopyUuid(printer.id)}
                                                aria-label="Copiar UUID da impressora"
                                            >
                                                <ContentCopy fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell>{printer.name}</TableCell>
                                <TableCell>{printer.brand}</TableCell>
                                <TableCell>{printer.model}</TableCell>
                                <TableCell>{printer.consumption} W</TableCell>
                                <TableCell>{formatCurrencyInCents(printer.price)}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={printer.active === false ? 'Inativa' : 'Ativa'}
                                        color={printer.active === false ? 'default' : 'success'}
                                        variant={printer.active === false ? 'outlined' : 'filled'}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEditPrinter(printer.id)}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handlePrinterMaintenances(printer.id)}
                                        aria-label="ver manutenções"
                                    >
                                        <Build />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeletePrinter(printer.id)}
                                        aria-label="remover impressora"
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2, // Espaçamento entre o Select e a paginação
                    mt: 3, // Margem superior para afastar do conteúdo acima
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
                    </>
                )}
            </Box>
        </div>
    );
}

export default Printers;
